import { Box, Tab, Tabs, Typography, getContrastRatio } from '@mui/material'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import "@fontsource/rubik";
import useCustomTheme from '../../theme/useCustomTheme'
import { generateLighterContrastColor } from '../../utils/commonServices'

export const REVISION_PANEL_TAB = { NOTES: 'notes', FILES: 'files' };

/**
 * Picks what the review card is showing: the revision notes, or the files
 * attached to the request.
 *
 * It sits at the top of the notes column because that is the column it swaps,
 * but the choice drives the whole card - the player's side becomes the file
 * list while Files is open. That is a bigger move than an underline on a word
 * suggests, so it is drawn as a segmented control: two pills in a track, the
 * live one lifted onto white. A switch, which is what it does, rather than a
 * link to somewhere further down the page.
 *
 * The file count rides along in a badge, so the user can see there is something
 * in there without opening it.
 */
const RevisionPanelTabs = (props) => {
  const { value, onChange, fileCount = 0 } = props;
  const [theme] = useCustomTheme();

  // The repo's standard reading of the brand colour: use it where it is legible
  // against the dark text, and a lightened variant where it is not.
  const accent = theme.palette.primary.main === ('#00d744' || '#00D744')
    ? '#00d744'
    : getContrastRatio('#08231B', theme.palette.primary.main) > 4.5
      ? `${theme.palette.primary.main}`
      : generateLighterContrastColor(theme.palette.primary.main);

  const label = (text, count) => (
    <Box className='flex a-center' gap={0.6} sx={{ minWidth: 0 }}>
      <Typography
        component='span'
        sx={{ fontFamily: 'Rubik', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 'inherit', color: 'inherit' }}
      >
        {text}
      </Typography>
      {/* Zero would read as a defect rather than as information, so the badge
          appears only once there is something to count. */}
      {count > 0 && (
        <Box
          data-testid='revision-tab-files-count'
          sx={{
            minWidth: { xs: '12px', md: '14px', lg: '16px', xl: '22px' },
            height: { xs: '12px', md: '14px', lg: '16px', xl: '22px' },
            px: '3px',
            borderRadius: '999px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: value === REVISION_PANEL_TAB.FILES ? accent : '#08231B1f',
            color: '#08231B',
            fontFamily: 'Rubik', fontWeight: 600, lineHeight: 1,
            fontSize: { xs: '6px', md: '7px', lg: '8px', xl: '11px' },
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        margin: { xs: '10px 10px 0', xl: '14px 14px 0' },
        padding: { xs: '3px', xl: '4px' },
        borderRadius: '999px',
        backgroundColor: '#08231B0a',
        border: '1px solid #08231B14',
      }}
    >
      <Tabs
        value={value}
        onChange={(event, newValue) => onChange(newValue)}
        data-testid='revision-panel-tabs'
        variant='fullWidth'
        // The lifted pill marks the live tab; an underline underneath it as well
        // would be the same thing said twice.
        TabIndicatorProps={{ sx: { display: 'none' } }}
        sx={{
          minHeight: 0,
          '& .MuiTabs-flexContainer': { gap: { xs: '3px', xl: '4px' } },
          '& .MuiTab-root': {
            fontFamily: 'Rubik',
            fontWeight: 600,
            textTransform: 'none',
            color: '#08231B',
            opacity: 0.55,
            minHeight: { xs: '22px', md: '26px', lg: '30px', xl: '40px' },
            minWidth: 0,
            padding: { xs: '0 6px', xl: '0 12px' },
            borderRadius: '999px',
            fontSize: { xs: '8px', md: '10px', lg: '11px', xl: '15px' },
            transition: 'background-color 160ms ease, box-shadow 160ms ease, opacity 160ms ease',
            '& .MuiTab-iconWrapper': {
              marginRight: { xs: '3px', xl: '6px' },
              fontSize: { xs: '9px', md: '11px', lg: '13px', xl: '18px' },
            },
          },
          '&:hover .MuiTab-root:not(.Mui-selected)': { opacity: 0.8 },
          '& .Mui-selected': {
            opacity: 1,
            color: '#08231B !important',
            backgroundColor: '#ffffff',
            boxShadow: '0px 2px 6px #00000014',
          },
          // Keyboard-only, so tabbing stays visible without ringing every click.
          '& .MuiTab-root:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: '-2px' },
        }}
      >
        <Tab
          label={label('Revision Notes')}
          value={REVISION_PANEL_TAB.NOTES}
          data-testid='revision-tab-notes'
          icon={<StickyNote2OutlinedIcon />}
          iconPosition='start'
          disableRipple
        />
        <Tab
          label={label('Files', fileCount)}
          value={REVISION_PANEL_TAB.FILES}
          data-testid='revision-tab-files'
          icon={<FolderOutlinedIcon />}
          iconPosition='start'
          disableRipple
        />
      </Tabs>
    </Box>
  )
}

export default RevisionPanelTabs
